import React, { MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import * as colors from '../color';
import { mediaQuery } from '../breakpoints';
import LinkNoPrefetch from '../../components/linkNoPrefetch/LinkNoPrefetch';
import type { LocalPath } from '../../routes/components/path';

export enum fontSize {
  TINY = '0.6875rem', //'11px',
  SMALL_MINUS = '0.725rem', //'12px',
  SMALL = '0.8125rem', //'13px',
  MEDIUM_MINUS = '0.9375rem', //'15px',
  MEDIUM = '1rem', //'16px',
  LARGE = '1.125rem', //'18px',
}

export enum headingSize {
  TINY = '1.125rem', //'18px',
  EXTRASMALL = '1.3125rem', //'21px',
  SMALL = '1.5rem', //'24px',
  MEDIUM = '1.75rem', //'28px',
  LARGE = '2rem', //'32px',
  EXTRALARGE = '2.5rem', //'40px',
  HUGE = '3.75rem', //'60px',
}

export const linkStyle = css`
  color: ${({ theme }) => theme?.color.text.link};
  text-decoration: underline;
  text-underline-position: under;
  border-radius: ${({ theme }) => theme?.sizes.borderRadius.default};
  cursor: pointer;
  transition: outline-offset 0.1s;

  &:hover,
  &:focus {
    color: ${({ theme }) => (theme.isApp ? theme?.color.text.link : colors.linkHover)};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: ${({ theme }) => (theme.isApp ? 'none' : `2px solid ${colors.linkHover}`)};
    outline-offset: 2px;
    text-decoration: ${({ theme }) => (theme.isApp ? 'underline' : 'none')};
  }
`;

export const HrefLink = styled.a`
  ${linkStyle}
`;

export const StyledRouterLink = styled(LinkNoPrefetch)`
  ${linkStyle}
`;

export const Link: React.FC<{
  to?: LocalPath;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  children?: React.ReactNode;
}> = ({ to, href, onClick, children, ...props }) => {
  if (to) {
    return (
      <StyledRouterLink href={to} onClick={onClick} {...props}>
        {children}
      </StyledRouterLink>
    );
  } else if (href) {
    return (
      <HrefLink href={href} onClick={onClick} {...props}>
        {children}
      </HrefLink>
    );
  } else {
    return (
      <HrefLink tabIndex={onClick ? 0 : -1} onClick={onClick} {...props}>
        {children}
      </HrefLink>
    );
  }
};

export const linkInvertedStyle = css`
  color: ${({ theme }) => theme?.color.text.link};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: ${({ theme }) => (theme.isApp ? 'none' : 'underline')};
  }
`;

export const LinkInverted = styled.a`
  ${linkInvertedStyle}
`;

export const textStyle = css`
  font-family: ${({ theme }) => theme?.fontFamily?.body}, system-ui, sans-serif;
  font-feature-settings:
    'ss01' on,
    'ss03' on,
    'ss05' on;
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSize.MEDIUM};
  line-height: 110%;
  margin: 0;
  letter-spacing: 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

export const buttonStyle = css`
  ${textStyle}
  font-size: ${fontSize.MEDIUM_MINUS};
  font-weight: 600;
  line-height: 125%;
`;

export const Button = styled.span`
  ${buttonStyle}
`;

export const Text = styled.span`
  ${textStyle}
`;

export const textSmallStyle = css`
  ${textStyle}
  font-size: ${fontSize.SMALL};
`;

export const textXSmallStyle = css`
  ${textStyle}
  font-size: ${fontSize.SMALL_MINUS};
`;

export const TextSmall = styled.span`
  ${textSmallStyle}
`;

export const textTinyStyle = css`
  ${textStyle}
  font-size: ${fontSize.TINY};
`;

export const TextTiny = styled.span`
  ${textTinyStyle}
`;

export const textLargeStyle = css`
  ${textStyle}
  font-size: ${fontSize.LARGE};
`;

export const TextLarge = styled.span`
  ${textLargeStyle}
`;

export const headingStyle = css`
  font-family: ${({ theme }) => theme?.fontFamily?.heading}, system-ui, sans-serif;
  font-feature-settings: normal;
  font-style: normal;
  font-weight: 600;
  font-size: ${headingSize.SMALL};
  letter-spacing: -0.01em;
  text-transform: uppercase;
  line-height: 110%;
  margin: 0;
`;

export const headingTinyStyle = css`
  ${headingStyle}
  font-family: ${({ theme }) => theme?.fontFamily?.body}, system-ui, sans-serif;
  font-feature-settings:
    'ss01' on,
    'ss03' on,
    'ss05' on;
  text-transform: none;
  font-size: ${headingSize.TINY};

  @media ${mediaQuery.medium} {
    font-size: ${fontSize.MEDIUM};
  }
`;

export const HeadingTiny = styled.span`
  ${headingTinyStyle}
`;

export const headingSmallStyle = css`
  ${headingStyle}
  font-family: ${({ theme }) => theme?.fontFamily?.body}, system-ui, sans-serif;
  font-feature-settings:
    'ss01' on,
    'ss03' on,
    'ss05' on;
  text-transform: none;
  font-size: ${headingSize.EXTRASMALL};

  @media ${mediaQuery.medium} {
    font-size: ${fontSize.LARGE};
  }
`;

export const HeadingSmall = styled.span`
  ${headingSmallStyle}
`;

export const headingMediumStyle = css`
  ${headingStyle}
  font-size: ${headingSize.SMALL};

  @media ${mediaQuery.medium} {
    font-size: ${headingSize.EXTRASMALL};
  }
`;

export const HeadingMedium = styled.span`
  ${headingMediumStyle}
`;

export const headingLargeStyle = css`
  ${headingStyle}
  font-size: ${headingSize.LARGE};

  @media ${mediaQuery.medium} {
    font-size: ${headingSize.SMALL};
  }
`;

export const HeadingLarge = styled.span`
  ${headingLargeStyle}
`;

export const headingExtraLargeStyle = css`
  ${headingStyle}
  font-size: ${headingSize.EXTRALARGE};

  @media ${mediaQuery.medium} {
    font-size: ${headingSize.MEDIUM};
  }
`;

export const HeadingExtraLarge = styled.span`
  ${headingExtraLargeStyle}
`;

export const headingHugeStyle = css`
  ${headingStyle}
  font-size: ${headingSize.HUGE};

  @media ${mediaQuery.medium} {
    font-size: ${headingSize.EXTRALARGE};
  }
`;

export const HeadingHuge = styled.span`
  ${headingHugeStyle}
`;

export const overlineStyle = css`
  ${textStyle};
  font-size: ${fontSize.SMALL};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;

  @media ${mediaQuery.medium} {
    font-size: ${fontSize.TINY};
  }
`;

export const Overline = styled.span`
  ${overlineStyle}
`;

export const headingBrandStyle = css`
  ${headingStyle}

  font-family: ${({ theme }) => theme?.fontFamily?.headingBrand}, system-ui, sans-serif;
  letter-spacing: 0;
  text-transform: uppercase;
`;

export const headingBrandMediumStyle = css`
  ${headingBrandStyle}
  font-size: ${headingSize.MEDIUM};

  @media ${mediaQuery.medium} {
    font-size: ${headingSize.SMALL};
  }
`;

export const HeadingBrandMedium = styled.span`
  ${headingBrandMediumStyle}
`;

export const headingBrandSmallStyle = css`
  ${headingBrandStyle}
  font-size: ${headingSize.EXTRASMALL};

  @media ${mediaQuery.medium} {
    font-size: ${headingSize.TINY};
  }
`;

export const HeadingBrandSmall = styled.span`
  ${headingBrandSmallStyle}
`;

export const headingBrandLargeStyle = css`
  ${headingBrandStyle}
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
  font-size: ${headingSize.EXTRALARGE};

  @media ${mediaQuery.medium} {
    font-size: ${headingSize.MEDIUM};
  }
`;

export const HeadingBrandLarge = styled.span`
  ${headingBrandLargeStyle}
`;

export const headingBrandHugeStyle = css`
  ${headingBrandStyle}
  font-weight: 600;
  font-size: ${headingSize.HUGE};

  @media ${mediaQuery.medium} {
    font-size: ${headingSize.LARGE};
  }
`;

export const HeadingBrandHuge = styled.span`
  ${headingBrandHugeStyle}
`;

export const paragraphLargeStyle = css`
  ${textLargeStyle}
  line-height: 140%;
`;

export const ParagraphLarge = styled.p`
  ${paragraphLargeStyle}
`;

export const paragraphMediumStyle = css`
  ${textStyle}
  line-height: 140%;
`;

export const listMediumStyle = css`
  ${textStyle}
  line-height: 140%;
`;

export const ParagraphMedium = styled.p`
  ${paragraphMediumStyle}
`;

export const paragraphSmallStyle = css`
  ${textSmallStyle}
  line-height: 140%;
`;

export const ParagraphSmall = styled.p`
  ${paragraphSmallStyle}
`;

export const titleStyle = css`
  ${textStyle}
  line-height: 120%;
  font-weight: 600;
`;

export const Title = styled.span`
  ${titleStyle}
`;

export const titleLargeStyle = css`
  ${titleStyle}
  font-size: ${fontSize.LARGE};
`;

export const TitleLarge = styled.span`
  ${titleLargeStyle}
`;

export const inputLabelStyle = css`
  ${textSmallStyle}
  font-weight: 600;
`;

export const InputLabel = styled.span`
  ${inputLabelStyle}
`;

export const boldStyle = css`
  font-weight: bold;
`;

export const desktopNavigationStyle = css`
  ${textStyle}
  font-weight: 700;
  letter-spacing: 0;
  font-size: ${fontSize.LARGE};
`;
